import type { CSSProperties, ReactNode } from "react";
import type { DataElScope } from "@/utils/dataEl";
import { Cmarkdowntext } from "../Cmarkdowntext";
import { Ctext } from "../Ctext";
import { CtextfxReversingDecrypt } from "../Ctextfx";
import { TextPreset } from "@/constants/componentPresets";
import { FontWeight } from "@/theme/typography";
import {
  getCreferencePillInlineFlowParagraphStyle,
  getCreferencePillInlineFlowWrapperStyle,
  getCreferencePillLabelTextStyle,
  getCreferencePillTextColor,
  getCreferencePillTextOpacity,
  REFERENCE_PILL_LOADING_PLAY_KEY,
} from "./creferencePillContentModel";

const REFERENCE_PILL_LABEL_SIZE = 16;

export interface CreferencePillLabelProps {
  dataElScope: DataElScope;
  /** While the session payload is in flight the label is our `[loading]` word,
   *  not a name, so it breathes instead of sitting there looking resolved. */
  isLoading: boolean;
  isHovered: boolean;
  isUnresolved: boolean;
  linkedLabelTextStyle: CSSProperties;
  /** Version marker that outlived the truncation — rendered after the `..`. */
  suffix: string | null;
  text: string | null;
}

export function CreferencePillLabel({
  dataElScope,
  isLoading,
  isHovered,
  isUnresolved,
  linkedLabelTextStyle,
  suffix,
  text,
}: CreferencePillLabelProps): ReactNode {
  if (text == null || text.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Ctext
        preset={TextPreset.Regular}
        size={REFERENCE_PILL_LABEL_SIZE}
        weight={FontWeight.Heavy}
        color={getCreferencePillTextColor(isUnresolved)}
        opacity={getCreferencePillTextOpacity(isUnresolved)}
      >
        <CtextfxReversingDecrypt dataElScope={dataElScope} text={text} playKey={REFERENCE_PILL_LOADING_PLAY_KEY}/>
      </Ctext>
    );
  }

  // The label is model-authored (`product.name`, `version.contentTitle`,
  // `collab.shortestSummary`), so it renders as markdown like every other
  // model-authored surface. `CreferenceDepthBoundary` around this subtree is
  // what stops a label that itself holds a reference from recursing. The
  // marker joins the same text flow so it wraps with the name rather than
  // moving as a separate box.
  return (
    <Cmarkdowntext
      dataElScope={dataElScope}
      content={suffix == null ? text : `${text} ${suffix}`}
      size={REFERENCE_PILL_LABEL_SIZE}
      weight={FontWeight.Heavy}
      color={getCreferencePillTextColor(isUnresolved)}
      opacity={getCreferencePillTextOpacity(isUnresolved)}
      style={getCreferencePillInlineFlowWrapperStyle()}
      paragraphStyle={getCreferencePillInlineFlowParagraphStyle()}
      textStyle={getCreferencePillLabelTextStyle({
        isHovered,
        linkedLabelTextStyle,
      })}
    />
  );
}
